import { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CreditCard,
  Plus,
  Check,
  Clock,
  X,
  AlertCircle,
  Store,
  Banknote,
  FileText,
  Calendar,
  User,
  Loader2,
  Users,
  BookOpen
} from "lucide-react";
import PayoutLedgerDialog from "@/components/payouts/PayoutLedgerDialog";
import Pagination from "@/components/shared/Pagination";
import OwnerLedgerList from "@/components/payouts/OwnerLedgerList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The server answers errors as { message }; older handlers used { detail }.
const apiError = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.detail || fallback;

const PAYMENT_METHODS = [
  { value: "check", label: "Check", icon: Banknote },
  { value: "bank_transfer", label: "Bank Transfer", icon: CreditCard },
  { value: "other", label: "Other", icon: FileText }
];

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [distributorPayouts, setDistributorPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // The tab and each list's Pending/All view live in the URL (?tab=&rsub=&dsub=), so coming
  // Back from a ledger page lands on the same view. Unknown values fall back, because Radix
  // renders nothing for a value that has no matching trigger.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pick = (key, allowed, fallback) => (allowed.includes(searchParams.get(key)) ? searchParams.get(key) : fallback);
  const setParam = (key) => (value) =>
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set(key, value); return next; }, { replace: true });
  const activeTab = pick("tab", ["resellers", "distributors"], "resellers");
  const setActiveTab = setParam("tab");
  const resellerSub = pick("rsub", ["pending", "all"], "pending");
  const setResellerSub = setParam("rsub");
  const distributorSub = pick("dsub", ["pending", "all"], "pending");
  const setDistributorSub = setParam("dsub");
  const openLedgerPage = (kind, id) =>
    navigate(`/admin/payouts/ledger/${kind}/${encodeURIComponent(id)}`, {
      state: { from: `${location.pathname}${location.search}` }
    });

  // Create payout dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [createNotes, setCreateNotes] = useState("");

  // Create distributor payout dialog — same create-then-pay flow as resellers
  const [distDialogOpen, setDistDialogOpen] = useState(false);
  const [selectedDistributorId, setSelectedDistributorId] = useState(null);
  const [distAmount, setDistAmount] = useState("");
  const [distNotes, setDistNotes] = useState("");

  // Paging for the two payout history tables. The API returns each list whole, so it is
  // sliced here.
  const [resellerPage, setResellerPage] = useState(1);
  const [resellerPageSize, setResellerPageSize] = useState(10);
  const [distPage, setDistPage] = useState(1);
  const [distPageSize, setDistPageSize] = useState(10);

  // Payout ledger popup + the payout whose status change is in flight
  const [ledgerDialog, setLedgerDialog] = useState({ open: false, payoutId: null, kind: "reseller" });
  const [busyPayoutId, setBusyPayoutId] = useState(null);

  // Mark as paid dialog
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("check");
  const [checkNumber, setCheckNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  // Resellers who still have money owed. `pending_payout` already has both settled
  // payouts and unsettled (pending/processing) ones deducted by the API. "Owed" means at
  // least a cent - a floating-point leftover like 2e-13 is a settled balance.
  const payableEarnings = earnings.filter(
    (e) => e.store_id !== "0" && (e.pending_payout || 0) > 0.005
  );
  const selectedEarning = payableEarnings.find((e) => e.store_id === selectedStore) || null;
  const maxPayout = selectedEarning?.pending_payout || 0;

  // Distributors with money owed. `pending_earnings` already has anything in an unsettled
  // payout taken out (it drops when a payout is created).
  const payableDistributors = distributors.filter((d) => (d.pending_earnings || 0) > 0.005);
  const selectedDist = payableDistributors.find((d) => d.distributor_id === selectedDistributorId) || null;
  const maxDistPayout = selectedDist?.pending_earnings || 0;

  const paginate = (items, page, pageSize) => {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    // A refetch can shrink the list under the page you're on, so clamp instead of showing nothing.
    const current = Math.min(page, totalPages);
    return { rows: items.slice((current - 1) * pageSize, current * pageSize), page: current, totalPages };
  };
  const resellerPayouts = paginate(payouts, resellerPage, resellerPageSize);
  const distPayouts = paginate(distributorPayouts, distPage, distPageSize);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [payoutsRes, earningsRes, distRes, distPayoutsRes] = await Promise.all([
        axios.get(`${API}/admin/payouts`, getAuthHeaders()),
        axios.get(`${API}/earnings/all`, getAuthHeaders()),
        axios.get(`${API}/admin/distributors`, getAuthHeaders()),
        axios.get(`${API}/admin/distributor-payouts`, getAuthHeaders())
      ]);
      setPayouts(payoutsRes.data);
      setEarnings(earningsRes.data.reseller_earnings || []);
      setDistributors(distRes.data || []);
      setDistributorPayouts(distPayoutsRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayout = async () => {
    if (!selectedStore || !payoutAmount) {
      toast.error("Please select a reseller and enter amount");
      return;
    }

    const amount = parseFloat(payoutAmount);
    if (!(amount > 0)) {
      toast.error("Enter a payout amount greater than zero");
      return;
    }
    // pending_payout already excludes anything committed to an unsettled payout.
    if (amount > maxPayout + 0.005) {
      toast.error(`Amount exceeds the $${maxPayout.toFixed(2)} available for this reseller`);
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/payouts/create`, {
        store_id: selectedStore,
        amount: parseFloat(payoutAmount),
        notes: createNotes
      }, getAuthHeaders());
      toast.success("Payout created successfully");
      setCreateDialogOpen(false);
      setSelectedStore(null);
      setPayoutAmount("");
      setCreateNotes("");
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.response?.data?.detail || "Failed to create payout"
      );
    } finally {
      setSaving(false);
    }
  };

  // Distributor payouts are created first (like resellers') and settled afterwards: one
  // "Create Payout" button, and the distributor is picked inside the dialog.
  const openDistributorCreateDialog = () => {
    setSelectedDistributorId(null);
    setDistAmount("");
    setDistNotes("");
    setDistDialogOpen(true);
  };

  const handleCreateDistributorPayout = async () => {
    if (!selectedDistributorId || !distAmount) {
      toast.error("Please select a distributor and enter amount");
      return;
    }

    const amount = parseFloat(distAmount);
    if (!(amount > 0)) {
      toast.error("Enter a payout amount greater than zero");
      return;
    }
    if (amount > maxDistPayout + 0.005) {
      toast.error(`Amount exceeds the $${maxDistPayout.toFixed(2)} available for this distributor`);
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/distributor-payouts`, {
        distributor_id: selectedDistributorId,
        amount,
        notes: distNotes || null
      }, getAuthHeaders());
      toast.success("Payout created successfully");
      setDistDialogOpen(false);
      setSelectedDistributorId(null);
      setDistAmount("");
      setDistNotes("");
      fetchData();
    } catch (error) {
      toast.error(apiError(error, "Failed to create payout"));
    } finally {
      setSaving(false);
    }
  };

  // `kind` is "reseller" | "distributor"; the two payout shapes name their owner differently.
  const openMarkPaidDialog = (payout, kind = "reseller") => {
    setSelectedPayout(
      kind === "distributor"
        ? {
            ...payout,
            type: 'Distributor',
            business_name: payout.distributor_name,
            subdomain: payout.distributor_email
          }
        : { ...payout, type: 'Funeral Home' }
    );
    setPaymentMethod("check");
    setCheckNumber("");
    setReferenceNumber("");
    setPaymentNotes("");
    setMarkPaidDialogOpen(true);
  };

  const openLedger = (payout, kind) => setLedgerDialog({ open: true, payoutId: payout.payout_id, kind });

  const handleMarkAsPaid = async () => {
    if (!selectedPayout) return;

    if (paymentMethod === "check" && !checkNumber) {
      toast.error("Please enter the check number");
      return;
    }

    setSaving(true);
    try {
      const path = selectedPayout.type === 'Distributor' ? "distributor-payouts" : "payouts";
      await axios.put(`${API}/admin/${path}/${selectedPayout.payout_id}/status`, {
        status: "completed",
        payment_method: paymentMethod,
        check_number: checkNumber || null,
        reference_number: referenceNumber || null,
        notes: paymentNotes || null
      }, getAuthHeaders());
      toast.success("Payout marked as paid!");

      setMarkPaidDialogOpen(false);
      setSelectedPayout(null);
      fetchData();
    } catch (error) {
      toast.error(apiError(error, "Failed to update payout"));
    } finally {
      setSaving(false);
    }
  };

  const updatePayoutStatus = async (payout, status, kind = "reseller") => {
    // Failing/cancelling hands the earnings back and can't be undone.
    if (status === "failed" || status === "cancelled") {
      const verb = status === "failed" ? "mark this payout as failed" : "cancel this payout";
      if (!window.confirm(`Are you sure you want to ${verb}? The earnings return to the available balance.`)) return;
    }

    setBusyPayoutId(payout.payout_id);
    try {
      const path = kind === "distributor" ? "distributor-payouts" : "payouts";
      await axios.put(`${API}/admin/${path}/${payout.payout_id}/status`, { status }, getAuthHeaders());
      toast.success(`Payout marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error(apiError(error, "Failed to update payout status"));
    } finally {
      setBusyPayoutId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      cancelled: 'bg-slate-100 text-slate-600'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <Check className="w-3 h-3" />;
      case 'processing': return <Clock className="w-3 h-3" />;
      case 'failed':
      case 'cancelled': return <X className="w-3 h-3" />;
      default: return <AlertCircle className="w-3 h-3" />;
    }
  };

  // Calculate total pending payouts
  const totalPending = earnings.reduce((sum, e) => sum + (e.pending_payout || 0), 0);
  const totalPaidOut = earnings.reduce((sum, e) => sum + (e.paid_out || 0), 0);

  // Calculate distributor totals
  const totalDistPending = distributors.reduce((sum, d) => sum + (d.pending_earnings || 0), 0);
  // pending_earnings already drops when a payout is created, so money in a payout that
  // hasn't completed yet (in_payout) is neither pending nor paid.
  const totalDistPaid = distributors.reduce((sum, d) => sum + Math.max(0, (d.total_earnings || 0) - (d.pending_earnings || 0) - (d.in_payout || 0)), 0);

  // Distributor payout handled via generic dialog

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-payouts">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          Payouts
        </h2>
        <p className="text-slate-500">Track and process commission payments</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="resellers" className="gap-2">
            <Store className="w-4 h-4" />
            Reseller Payouts
          </TabsTrigger>
          <TabsTrigger value="distributors" className="gap-2">
            <Users className="w-4 h-4" />
            Distributor Payouts
          </TabsTrigger>
        </TabsList>

        {/* Reseller Payouts Tab */}
        <TabsContent value="resellers" className="space-y-6">
          <div className="flex justify-end">
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Payout
            </Button>
          </div>

          {/* Reseller Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Payouts</p>
                  <p className="text-2xl font-bold text-slate-900">${totalPending.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Paid Out</p>
                  <p className="text-2xl font-bold text-slate-900">${totalPaidOut.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Store className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Resellers</p>
                  <p className="text-2xl font-bold text-slate-900">{earnings.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Resellers: Pending / All views of one table, each row with a Ledger button */}
          <OwnerLedgerList
            kind="reseller"
            rows={earnings.filter((e) => e.store_id !== "0")}
            sub={resellerSub}
            onSubChange={setResellerSub}
            onOpenLedger={openLedgerPage}
          />

          {/* Payouts Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-slate-900">Payout History</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Reseller</TableHead>
                  <TableHead className="font-semibold">Amount</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Payment Details</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <Banknote className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>No payouts yet</p>
                      <p className="text-sm">Create one when resellers have pending earnings</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  resellerPayouts.rows.map((payout) => (
                    <TableRow key={payout.payout_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{payout.business_name}</p>
                          <p className="text-xs text-slate-500">{payout.subdomain}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-lg font-bold text-amber-600">${payout.amount.toFixed(2)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadge(payout.status)} flex items-center gap-1 w-fit`}>
                          {getStatusIcon(payout.status)}
                          {payout.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {payout.status === 'completed' ? (
                          <div className="text-sm">
                            <p className="text-slate-700">
                              {payout.method === 'check' && payout.method_details?.check_number && (
                                <span className="flex items-center gap-1">
                                  <Banknote className="w-3 h-3" />
                                  Check #{payout.method_details.check_number}
                                </span>
                              )}
                              {payout.method === 'bank_transfer' && (
                                <span className="flex items-center gap-1">
                                  <CreditCard className="w-3 h-3" />
                                  Bank Transfer
                                </span>
                              )}
                              {(payout.reference_number || payout.method_details?.reference_number) && (
                                <span className="text-slate-500 text-xs block">Ref: {payout.reference_number || payout.method_details?.reference_number}</span>
                              )}
                            </p>
                            {payout.notes && (
                              <p className="text-slate-500 text-xs mt-1 truncate max-w-[200px]" title={payout.notes}>
                                {payout.notes}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="text-slate-700">{formatDate(payout.created_at)}</p>
                          {payout.processed_at && (
                            <p className="text-xs text-slate-500">
                              Paid: {formatDate(payout.processed_at)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <PayoutActions
                          payout={payout}
                          busy={busyPayoutId === payout.payout_id}
                          onStatus={(status) => updatePayoutStatus(payout, status, "reseller")}
                          onMarkPaid={() => openMarkPaidDialog(payout, "reseller")}
                          onLedger={() => openLedger(payout, "reseller")}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {payouts.length > 0 && (
              <div className="p-4 border-t">
                <Pagination
                  page={resellerPayouts.page}
                  totalPages={resellerPayouts.totalPages}
                  onChange={setResellerPage}
                  totalItems={payouts.length}
                  pageSize={resellerPageSize}
                  onPageSizeChange={(size) => { setResellerPageSize(size); setResellerPage(1); }}
                />
              </div>
            )}
          </div>
        </TabsContent>

        {/* Distributor Payouts Tab */}
        <TabsContent value="distributors" className="space-y-6">
          <div className="flex justify-end">
            <Button
              onClick={openDistributorCreateDialog}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Payout
            </Button>
          </div>

          {/* Distributor Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Commissions</p>
                  <p className="text-2xl font-bold text-slate-900">${totalDistPending.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Paid Out</p>
                  <p className="text-2xl font-bold text-slate-900">${totalDistPaid.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Distributors</p>
                  <p className="text-2xl font-bold text-slate-900">{distributors.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Distributors: Pending / All views of one table, each row with a Ledger button */}
          <OwnerLedgerList
            kind="distributor"
            rows={distributors}
            sub={distributorSub}
            onSubChange={setDistributorSub}
            onOpenLedger={openLedgerPage}
          />

          {/* Distributor Payout History */}
          {distributorPayouts.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-slate-900">Payout History</h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Distributor</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Payment Details</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {distributorPayouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                        <Banknote className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p>No payouts yet</p>
                        <p className="text-sm">Create one when distributors have pending earnings</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    distPayouts.rows.map((payout) => (
                      <TableRow key={payout.payout_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{payout.distributor_name}</p>
                            <p className="text-xs text-slate-500">{payout.distributor_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-lg font-bold text-amber-600">${(payout.amount || 0).toFixed(2)}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadge(payout.status)} flex items-center gap-1 w-fit`}>
                            {getStatusIcon(payout.status)}
                            {payout.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {payout.status === 'completed' ? (
                            <div className="text-sm">
                              <p className="text-slate-700">
                                {payout.method === 'check' && payout.method_details?.check_number && (
                                  <span className="flex items-center gap-1">
                                    <Banknote className="w-3 h-3" />
                                    Check #{payout.method_details.check_number}
                                  </span>
                                )}
                                {payout.method === 'bank_transfer' && (
                                  <span className="flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" />
                                    Bank Transfer
                                  </span>
                                )}
                                {(payout.reference_number || payout.method_details?.reference_number) && (
                                  <span className="text-slate-500 text-xs block">Ref: {payout.reference_number || payout.method_details?.reference_number}</span>
                                )}
                              </p>
                              {payout.notes && (
                                <p className="text-slate-500 text-xs mt-1 truncate max-w-[200px]" title={payout.notes}>
                                  {payout.notes}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="text-slate-700">{formatDate(payout.created_at)}</p>
                            {payout.processed_at && (
                              <p className="text-xs text-slate-500">
                                Paid: {formatDate(payout.processed_at)}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <PayoutActions
                            payout={payout}
                            busy={busyPayoutId === payout.payout_id}
                            onStatus={(status) => updatePayoutStatus(payout, status, "distributor")}
                            onMarkPaid={() => openMarkPaidDialog(payout, "distributor")}
                            onLedger={() => openLedger(payout, "distributor")}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <div className="p-4 border-t">
                <Pagination
                  page={distPayouts.page}
                  totalPages={distPayouts.totalPages}
                  onChange={setDistPage}
                  totalItems={distributorPayouts.length}
                  pageSize={distPageSize}
                  onPageSizeChange={(size) => { setDistPageSize(size); setDistPage(1); }}
                />
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Payout Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Create Payout
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Select Reseller</Label>
              <Select value={selectedStore || ""} onValueChange={setSelectedStore}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a reseller" />
                </SelectTrigger>
                <SelectContent>
                  {payableEarnings.map((earning) => (
                    <SelectItem key={earning.store_id} value={earning.store_id}>
                      <div className="flex items-center justify-between w-full gap-4">
                        <span>{earning.business_name}</span>
                        <span className="text-amber-600 font-medium">
                          ${(earning.pending_payout || 0).toFixed(2)} owed
                          {(earning.reserved_payout || 0) > 0 && (
                            <span className="text-slate-400 font-normal ml-1">
                              (${earning.reserved_payout.toFixed(2)} in a pending payout)
                            </span>
                          )}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  {payableEarnings.length === 0 && (
                    <div className="px-2 py-3 text-sm text-slate-500">
                      No reseller has an unpaid balance right now.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Payout Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={selectedEarning ? maxPayout : undefined}
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
              />
              {selectedEarning && (
                <p className="text-xs text-slate-500">
                  Available to pay: ${maxPayout.toFixed(2)}
                  {(selectedEarning.reserved_payout || 0) > 0 &&
                    ` — $${selectedEarning.reserved_payout.toFixed(2)} is already committed to a pending payout`}
                </p>
              )}
              {selectedEarning && parseFloat(payoutAmount) > maxPayout + 0.005 && (
                <p className="text-xs text-red-600">
                  Amount exceeds the available balance.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                placeholder="Any notes about this payout..."
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreatePayout}
                disabled={saving}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Payout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Distributor Payout Dialog */}
      <Dialog open={distDialogOpen} onOpenChange={setDistDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Create Distributor Payout
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Select Distributor</Label>
              <Select value={selectedDistributorId || ""} onValueChange={setSelectedDistributorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a distributor" />
                </SelectTrigger>
                <SelectContent>
                  {payableDistributors.map((dist) => (
                    <SelectItem key={dist.distributor_id} value={dist.distributor_id}>
                      <div className="flex items-center justify-between w-full gap-4">
                        <span>{dist.name}</span>
                        <span className="text-amber-600 font-medium">
                          ${(dist.pending_earnings || 0).toFixed(2)} owed
                          {(dist.in_payout || 0) > 0 && (
                            <span className="text-slate-400 font-normal ml-1">
                              (${dist.in_payout.toFixed(2)} in a pending payout)
                            </span>
                          )}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  {payableDistributors.length === 0 && (
                    <div className="px-2 py-3 text-sm text-slate-500">
                      No distributor has an unpaid balance right now.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Payout Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={selectedDist ? maxDistPayout : undefined}
                value={distAmount}
                onChange={(e) => setDistAmount(e.target.value)}
                placeholder="Enter amount"
              />
              {selectedDist && (
                <p className="text-xs text-slate-500">
                  Available to pay: ${maxDistPayout.toFixed(2)}
                  {(selectedDist.in_payout || 0) > 0 &&
                    ` — $${selectedDist.in_payout.toFixed(2)} is already committed to a pending payout`}
                </p>
              )}
              {selectedDist && parseFloat(distAmount) > maxDistPayout + 0.005 && (
                <p className="text-xs text-red-600">
                  Amount exceeds the available balance.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={distNotes}
                onChange={(e) => setDistNotes(e.target.value)}
                placeholder="Any notes about this payout..."
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setDistDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreateDistributorPayout}
                disabled={saving}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Payout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payout ledger (credits settled + the payout debit) */}
      <PayoutLedgerDialog
        open={ledgerDialog.open}
        onClose={() => setLedgerDialog((d) => ({ ...d, open: false }))}
        payoutId={ledgerDialog.payoutId}
        payoutKind={ledgerDialog.kind}
        viewer="admin"
      />

      {/* Mark as Paid Dialog */}
      <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              Mark as Paid ({selectedPayout?.type})
            </DialogTitle>
          </DialogHeader>

          {selectedPayout && (
            <div className="space-y-4 mt-4">
              {/* Payout Summary */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">{selectedPayout.business_name}</p>
                    <p className="text-sm text-slate-500">{selectedPayout.subdomain}</p>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">${selectedPayout.amount.toFixed(2)}</p>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        <div className="flex items-center gap-2">
                          <method.icon className="w-4 h-4" />
                          {method.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Check Number (if check selected) */}
              {paymentMethod === "check" && (
                <div className="space-y-2">
                  <Label>Check Number *</Label>
                  <Input
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    placeholder="e.g., 1234"
                  />
                </div>
              )}

              {/* Reference Number */}
              <div className="space-y-2">
                <Label>Reference / Confirmation Number (Optional)</Label>
                <Input
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="e.g., transaction ID, wire reference"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Payment Notes</Label>
                <Textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g., Mailed on 01/06/2025, sent to address on file..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setMarkPaidDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleMarkAsPaid}
                  disabled={saving}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Confirm Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Row actions shared by the reseller and distributor payout tables — both sides follow
// the same lifecycle: pending -> processing -> paid, and a pending/processing payout can
// be cancelled/failed, which hands the earnings back to the available balance.
function PayoutActions({ payout, busy, onStatus, onMarkPaid, onLedger }) {
  const { status } = payout;
  return (
    <div className="flex items-center justify-end gap-2 flex-wrap">
      {status === 'pending' && (
        <>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onStatus('processing')}>
            <Clock className="w-4 h-4 mr-1" />
            Processing
          </Button>
          <Button size="sm" disabled={busy} onClick={onMarkPaid} className="bg-green-600 hover:bg-green-700">
            <Check className="w-4 h-4 mr-1" />
            Mark Paid
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onStatus('cancelled')}
            className="text-red-600 border-red-200 hover:bg-red-50"
            title="Cancel this payout and return the earnings to the available balance"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        </>
      )}
      {status === 'processing' && (
        <>
          <Button size="sm" disabled={busy} onClick={onMarkPaid} className="bg-green-600 hover:bg-green-700">
            <Check className="w-4 h-4 mr-1" />
            Mark Paid
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onStatus('failed')}
            className="text-red-600 border-red-200 hover:bg-red-50"
            title="Mark as failed and return the earnings to the available balance"
          >
            <X className="w-4 h-4" />
          </Button>
        </>
      )}
      {status === 'completed' && (
        <Badge variant="outline" className="text-green-600">
          <Check className="w-3 h-3 mr-1" />
          Paid
        </Badge>
      )}
      <Button size="sm" variant="ghost" onClick={onLedger} title="See the ledger entries for this payout">
        <BookOpen className="w-4 h-4 mr-1" />
        Ledger
      </Button>
    </div>
  );
}
